import {
  type BuildIntercomReplaySuite,
  type EvalSuiteDetail,
  EvalSuiteDetailSchema,
  type EvalSuitePage,
  EvalSuitePageSchema,
  type EvalSuiteSpec,
  EvalTaskSchema,
  type EvalTaskSpec,
  IntercomReplaySuiteBuiltSchema,
  type ListEvalSuitesQuery,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiSend } from './client';

const EVAL_SUITES_KEY = ['admin', 'eval-suites'];

function listKey(query: ListEvalSuitesQuery) {
  return [...EVAL_SUITES_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...EVAL_SUITES_KEY, 'detail', id] as const;
}

export function useEvalSuites(query: ListEvalSuitesQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) {
    params.set('search', query.search);
  }
  return useQuery<EvalSuitePage>({
    queryKey: listKey(query),
    queryFn: () => apiGet(`/api/admin/eval-suites?${params.toString()}`, EvalSuitePageSchema),
  });
}

export function useEvalSuite(id: string, options?: { enabled?: boolean }) {
  return useQuery<EvalSuiteDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/eval-suites/${id}`, EvalSuiteDetailSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EvalSuiteSpec) =>
      apiSend('POST', '/api/admin/eval-suites', input, EvalSuiteDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useUpdateEvalSuite(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EvalSuiteSpec) =>
      apiSend('PATCH', `/api/admin/eval-suites/${id}`, input, EvalSuiteDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useDeleteEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/eval-suites/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useBuildIntercomReplaySuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BuildIntercomReplaySuite) =>
      apiSend(
        'POST',
        '/api/admin/draft-shadow/replay-suite',
        input,
        IntercomReplaySuiteBuiltSchema,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useAddTask(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EvalTaskSpec) =>
      apiSend('POST', `/api/admin/eval-suites/${suiteId}/tasks`, input, EvalTaskSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useUpdateTask(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: EvalTaskSpec }) =>
      apiSend('PATCH', `/api/admin/eval-suites/${suiteId}/tasks/${taskId}`, input, EvalTaskSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}

export function useDeleteTask(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => apiDelete(`/api/admin/eval-suites/${suiteId}/tasks/${taskId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_SUITES_KEY }),
  });
}
